import { useState, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import imageCompression from "browser-image-compression";
import { showNotification } from "../redux/slices/notificationSlice";
import { logDurationEvent } from "../analytics";

interface ImageType {
  src?: string;
  fileId?: string;
  url: string;
  isLoading: boolean;
}

const ModerationErrorMapping = {
  "inappropriate or sensitive content": {
    title: "Content Violation Detected",
    message: "In accordance with our content moderation policies, we are unable to process images that contain prohibited or harmful material. Please ensure that your uploads adhere to our guidelines.",
  },
  "PII content": {
    title: "Personal Information Detected",
    message: "In compliance with our privacy policy, we are unable to process images containing personal information. Please refrain from uploading images that include any sensitive data.",
  },
};

const useUploadImages = () => {
  const [images, setImages] = useState<ImageType[]>([]);
  const cancelledUploadsRef = useRef<Map<string, boolean>>(new Map());
  const dispatch = useDispatch();

  const isImageLoading = useMemo(() => images.some(image => image.isLoading), [images]);

  const addImage = (image: ImageType) => {
    setImages(prev => prev.concat(image));
  };

  const updateImageSrc = (fileId: string, src: string) => {
    setImages(prevImages => prevImages.map((image) => {
      return fileId === image.fileId ? { ...image, src, isLoading: false } : image;
    }));
  };

  const updateFileId = (url: string, fileId: string) => {
    setImages(prevImages => prevImages.map((image) => {
      return url === image.url && !image.fileId ? { ...image, fileId } : image;
    }));
  };

  const uploadImageFiles = (files: File[]) => {
    files.forEach(async (file) => {
      if (file.type.startsWith("image/")) {
        if (file.type !== "image/webp" && file.type !== "image/gif") {
          const compressedFile = await imageCompression(file, { maxWidthOrHeight: 1024 });
          uploadImage(compressedFile);
        }
        else {
          uploadImage(file);
        }
      }
    });
  };

  const uploadImage = async (file: File) => {
    const startTime = performance.now();
    let fileId;
    const url = URL.createObjectURL(file);
    cancelledUploadsRef.current.set(url, false);

    try {
      addImage({
        isLoading: true,
        url,
      });
      const payload = {
        file_name: file.name,
      };
      // step 1 - create file ID
      const { data } = await axios.post(`${process.env.REACT_APP_SPL_URL}/v1/tradegpt/chats/files`, payload, { withCredentials: true });
      fileId = data.file_id;

      // check if the image has been removed before fileId is created
      if (cancelledUploadsRef.current.get(url)) {
        deleteImage(url, data.file_id);
        return;
      }
      updateFileId(url, fileId);

      // step 2 - upload file content
      const response = await axios.put(data.upload_url, file, {
        headers: {
          "Content-Type": file.type,
        },
      });

      if (cancelledUploadsRef.current.get(url)) return;

      // step 3 - mark upload complete
      if (response.status === 200 || response.status === 204) {
        const { data: imageData } = await axios.put(`${process.env.REACT_APP_SPL_URL}/v1/tradegpt/chats/files/${data.file_id}/upload`, {
          status: "uploaded",
        }, { withCredentials: true });
        const endTime = performance.now();
        const durationSeconds = (endTime - startTime) / 1000;
        updateImageSrc(imageData.file_id, imageData.download_url);
        logDurationEvent("multimedia_upload_duration", durationSeconds, { media_type: file.type, media_file_size: file.size });
      }
      else {
        removeImage(url);
        dispatch(showNotification({ title: "Something Unexpected Happened",
          message: "An error has occurred while processing your request. Try refreshing the page or attempt the action again. If the problem persists, please contact our support team.",
          severity: "error",
          horizontal: "center" }));
      }
    }
    catch (err) {
      // don't show notification if image was intentionally cancelled by user
      const wasCancelled = cancelledUploadsRef.current.get(url);
      if (wasCancelled) {
        return;
      }

      let errorObj = { title: "Something Unexpected Happened", message: "An error has occurred while processing your request. Try refreshing the page or attempt the action again. If the problem persists, please contact our support team." };
      if (err.status === 422) {
        const errorMapping = ModerationErrorMapping[err.response?.data?.message];
        if (errorMapping) {
          errorObj = errorMapping;
        }
        removeImage(url);
      }
      else {
        deleteImage(url, fileId);
      }
      dispatch(showNotification({ ...errorObj, severity: "error", horizontal: "center" }));
    }
  };

  const removeImage = (urlToRemove: string, fileId?: string) => {
    cancelledUploadsRef.current.set(urlToRemove, true);
    deleteImage(urlToRemove, fileId);
  };
  const deleteImage = (url: string, fileId: string) => {
    setImages(prev => prev.filter(img => img.url !== url));
    if (fileId) {
      try {
        axios.delete(`${process.env.REACT_APP_SPL_URL}/v1/tradegpt/chats/files/${fileId}`, { withCredentials: true });
      }
      catch {
        console.error("Delete image failed");
      }
    }
  };

  const clearImages = () => {
    if (images.length) {
      images.forEach((image) => {
        URL.revokeObjectURL(image.url);
      });
    }
    setImages([]);
  };

  return {
    images,
    isImageLoading,
    deleteImage,
    removeImage,
    clearImages,
    uploadImageFiles,
  };
};

export default useUploadImages;
